import { IDxfBounds } from './dxf-entities';
import { EDxfLinetype, IDxfLayer } from './dxf-layers';
import { DXF_NO_OWNER, IDxfOut, beginSection, coord, endSection, int, pair, real, takeHandle } from './dxf-out';

/** The one text style of the file. Arial carries Cyrillic in every CAD that reads TrueType. */
export const DXF_TEXT_STYLE: string = 'Standard';
const DXF_TEXT_FONT_FILE: string = 'arial.ttf';
const DXF_TEXT_FONT_FAMILY: string = 'Arial';
/** XDATA flags of a TrueType font record: regular, not bold or italic, with the family's pitch and charset. */
const TRUETYPE_FLAGS: number = 34;

/** The window the file opens on: the view takes this much more than the drawing's extents. */
const VIEW_MARGIN: number = 1.1;
/** The aspect ratio the opening view assumes, width over height — a usual landscape screen. */
const VIEW_ASPECT: number = 1.6;

/** A linetype of ГОСТ 2.303 in paper millimetres: dash lengths positive, gaps negative, a dot zero. */
interface ILinetype {
    name: string;
    description: string;
    pattern: readonly number[];
}

const LINETYPES: readonly ILinetype[] = [
    { name: 'ByBlock', description: '', pattern: [] },
    { name: 'ByLayer', description: '', pattern: [] },
    { name: EDxfLinetype.Continuous, description: 'Solid line', pattern: [] },
    { name: EDxfLinetype.Dashed, description: 'GOST 2.303 hidden __ __ __', pattern: [4, -1.5] },
    { name: EDxfLinetype.DashDot, description: 'GOST 2.303 axis ___ . ___ .', pattern: [12, -3, 0, -3] },
];

/** The handles the tables point to that live elsewhere in the file: the two layouts. */
export interface IDxfLayoutHandles {
    modelLayout: string;
    paperLayout: string;
}

/** The handles of the two block records, the owners of model-space and paper-space entities. */
export interface IDxfBlockOwners {
    modelSpace: string;
    paperSpace: string;
}

/** Opens a symbol table and returns its handle, the owner of its records. */
function beginTable(out: IDxfOut, name: string, count: number): string {
    const handle: string = takeHandle(out);
    pair(out, 0, 'TABLE');
    pair(out, 2, name);
    pair(out, 5, handle);
    pair(out, 330, DXF_NO_OWNER);
    pair(out, 100, 'AcDbSymbolTable');
    int(out, 70, count);
    return handle;
}

function endTable(out: IDxfOut): void {
    pair(out, 0, 'ENDTAB');
}

function beginRecord(out: IDxfOut, type: string, owner: string, subclass: string, handleCode: number = 5): string {
    const handle: string = takeHandle(out);
    pair(out, 0, type);
    pair(out, handleCode, handle);
    pair(out, 330, owner);
    pair(out, 100, 'AcDbSymbolTableRecord');
    pair(out, 100, subclass);
    return handle;
}

function emptyTable(out: IDxfOut, name: string): void {
    beginTable(out, name, 0);
    endTable(out);
}

/** The viewport record AutoCAD opens model space with: `*ACTIVE`, centred on the drawing with a margin. */
function writeViewports(out: IDxfOut, extents: IDxfBounds): void {
    const width: number = extents.maxX - extents.minX;
    const height: number = extents.maxY - extents.minY;
    const owner: string = beginTable(out, 'VPORT', 1);
    beginRecord(out, 'VPORT', owner, 'AcDbViewportTableRecord');
    pair(out, 2, '*ACTIVE');
    int(out, 70, 0);
    coord(out, 10, 0);
    coord(out, 20, 0);
    coord(out, 11, 1);
    coord(out, 21, 1);
    coord(out, 12, (extents.minX + extents.maxX) / 2);
    coord(out, 22, (extents.minY + extents.maxY) / 2);
    coord(out, 40, Math.max(height, width / VIEW_ASPECT) * VIEW_MARGIN);
    real(out, 41, VIEW_ASPECT);
    endTable(out);
}

function writeLinetypes(out: IDxfOut): void {
    const owner: string = beginTable(out, 'LTYPE', LINETYPES.length);
    for (const linetype of LINETYPES) {
        beginRecord(out, 'LTYPE', owner, 'AcDbLinetypeTableRecord');
        pair(out, 2, linetype.name);
        int(out, 70, 0);
        pair(out, 3, linetype.description);
        int(out, 72, 65);
        int(out, 73, linetype.pattern.length);
        real(
            out,
            40,
            linetype.pattern.reduce((sum: number, length: number): number => sum + Math.abs(length), 0)
        );
        for (const length of linetype.pattern) {
            real(out, 49, length);
            int(out, 74, 0);
        }
    }
    endTable(out);
}

function writeLayers(out: IDxfOut, layers: readonly IDxfLayer[]): void {
    const owner: string = beginTable(out, 'LAYER', layers.length);
    for (const layer of layers) {
        beginRecord(out, 'LAYER', owner, 'AcDbLayerTableRecord');
        pair(out, 2, layer.name);
        int(out, 70, 0);
        // A negative colour switches the layer off and keeps its colour for when it is turned on.
        int(out, 62, layer.on ? layer.color : -layer.color);
        pair(out, 6, layer.linetype);
        int(out, 290, layer.plot ? 1 : 0);
        int(out, 370, layer.lineweight);
    }
    endTable(out);
}

function writeTextStyles(out: IDxfOut): void {
    const owner: string = beginTable(out, 'STYLE', 1);
    beginRecord(out, 'STYLE', owner, 'AcDbTextStyleTableRecord');
    pair(out, 2, DXF_TEXT_STYLE);
    int(out, 70, 0);
    real(out, 40, 0);
    real(out, 41, 1);
    real(out, 50, 0);
    int(out, 71, 0);
    real(out, 42, 2.5);
    pair(out, 3, DXF_TEXT_FONT_FILE);
    pair(out, 4, '');
    pair(out, 1001, 'ACAD');
    pair(out, 1000, DXF_TEXT_FONT_FAMILY);
    int(out, 1071, TRUETYPE_FLAGS);
    endTable(out);
}

function writeAppIds(out: IDxfOut): void {
    const owner: string = beginTable(out, 'APPID', 1);
    beginRecord(out, 'APPID', owner, 'AcDbRegAppTableRecord');
    pair(out, 2, 'ACAD');
    int(out, 70, 0);
    endTable(out);
}

function writeDimStyles(out: IDxfOut): void {
    const owner: string = beginTable(out, 'DIMSTYLE', 1);
    pair(out, 100, 'AcDbDimStyleTable');
    int(out, 71, 0);
    beginRecord(out, 'DIMSTYLE', owner, 'AcDbDimStyleTableRecord', 105);
    pair(out, 2, 'Standard');
    int(out, 70, 0);
    endTable(out);
}

function writeBlockRecord(out: IDxfOut, owner: string, name: string, layout: string): string {
    const handle: string = beginRecord(out, 'BLOCK_RECORD', owner, 'AcDbBlockTableRecord');
    pair(out, 2, name);
    pair(out, 340, layout);
    int(out, 70, 0);
    int(out, 280, 1);
    int(out, 281, 0);
    return handle;
}

function writeBlockRecords(out: IDxfOut, layouts: IDxfLayoutHandles): IDxfBlockOwners {
    const owner: string = beginTable(out, 'BLOCK_RECORD', 2);
    const modelSpace: string = writeBlockRecord(out, owner, '*Model_Space', layouts.modelLayout);
    const paperSpace: string = writeBlockRecord(out, owner, '*Paper_Space', layouts.paperLayout);
    endTable(out);
    return { modelSpace, paperSpace };
}

/** The TABLES section; returns the owners of the two spaces. */
export function writeTables(out: IDxfOut, layers: readonly IDxfLayer[], extents: IDxfBounds, layouts: IDxfLayoutHandles): IDxfBlockOwners {
    beginSection(out, 'TABLES');
    writeViewports(out, extents);
    writeLinetypes(out);
    writeLayers(out, layers);
    writeTextStyles(out);
    emptyTable(out, 'VIEW');
    emptyTable(out, 'UCS');
    writeAppIds(out);
    writeDimStyles(out);
    const owners: IDxfBlockOwners = writeBlockRecords(out, layouts);
    endSection(out);
    return owners;
}
